import { Message } from "@/types/message";
import { normalizeQuestion } from "./questionNormalizer";
import { debugLogger } from "@/utils/debugLogger";

// Helper function to check if a query is a simple follow-up
export const isSimpleFollowUp = (normalizedQuery: string): boolean => {
  const simpleFollowUpPatterns = [
    /tell me about \d+/,
    /\b(1|2|3|4|5|6|7|8|9|10)\b/,
    /^(tell me more|more about|what about|how about)/,
    /^(that one|this one|it)/,
    /^(details|info|information) about/
  ];
  
  return simpleFollowUpPatterns.some(pattern => pattern.test(normalizedQuery));
};

// Detect if question is a follow-up based on context
export const isFollowUpQuestion = (question: string, messages: Message[]): boolean => {
  const normalizedQuestion = normalizeQuestion(question);
  
  debugLogger.log(`🔍 Follow-up detection for: "${question}" -> normalized: "${normalizedQuestion}"`);
  
  // Common follow-up patterns - more comprehensive
  const followUpPatterns = [
    /^(tell me more|more about|what about|how about)/,
    /^(the (\d+)(st|nd|rd|th)|first|second|third|fourth|fifth)/,
    /(the (\d+)(st|nd|rd|th)|first|second|third|fourth|fifth)/, // Match anywhere in question
    /^(that one|this one|it)/,
    /^(details|info|information) about/,
    /^(where is|how do I get to|directions)/,
    /(tell me about|more info on|details on).*(1st|2nd|3rd|4th|5th|first|second|third|fourth|fifth|\d+)/,
    /tell me about \d+/, // "tell me about 1", "tell me about 2", etc.
    /\b(1|2|3|4|5|6|7|8|9|10)\b/, // Just numbers 1-10
    /\b(one|two|three|four|five)\b/ // Written numbers
  ];
  
  // Check if recent bot message contained recommendations (lists, numbered items, etc.)
  const lastBotMessage = messages.filter(m => m.isBot && !m.error).slice(-1)[0];
  const hasRecentRecommendations = lastBotMessage && 
    (lastBotMessage.content.includes('📍') || 
     lastBotMessage.content.includes('**') ||
     /\d+\./.test(lastBotMessage.content) || // Numbered lists like "1. Restaurant"
     lastBotMessage.content.includes('•')); // Bullet points
  
  debugLogger.log(`🔍 Recent bot message has recommendations: ${hasRecentRecommendations}`);
  debugLogger.log(`🔍 Last bot message preview: ${lastBotMessage?.content.substring(0, 100) || 'none'}...`);
  
  // Test each pattern
  const matchingPatterns = followUpPatterns.filter(pattern => pattern.test(normalizedQuestion));
  debugLogger.log(`🔍 Matching patterns count: ${matchingPatterns.length}`);
  
  const isFollowUp = followUpPatterns.some(pattern => pattern.test(normalizedQuestion)) && hasRecentRecommendations;
  debugLogger.log(`🔍 Final follow-up decision: ${isFollowUp}`);
  
  return isFollowUp;
};